/**
 * The signup wizard's controller: `signup.model.ts` wired to
 * `startSignupVerification`, `confirmSignupVerification` and
 * `resendSignupVerification`, with the sign-in itself going straight to
 * Supabase at the very end.
 *
 * It owns what the model cannot: the stage union (a ticket exists only once a
 * code has been sent, a result only once an account does), the typed values,
 * the busy and error pair, and a clock that ticks only while the resend
 * countdown is on screen.
 *
 * Sign-in is held back until the applicant presses Enter ONDO. The session
 * listener in App swaps the whole tree the moment a session appears, so
 * signing in earlier would flash past the confirmation they are meant to read.
 */

import { toggleDeclaredType } from "@ondo/shared";
import type {
  DeclaredMemberType,
  SignupResult,
  VerificationTicket,
} from "@ondo/shared";
import { useEffect, useState } from "react";

import {
  confirmSignupVerification,
  resendSignupVerification,
  startSignupVerification,
} from "../lib/api.ts";
import { supabase } from "../lib/supabase.ts";
import { SIGNUP_COPY } from "./signup.constants.ts";
import {
  EMPTY_DETAILS,
  invitedAddress,
  describeError,
  isCompleteCode,
  passwordsMatch,
  sanitiseCode,
  secondsUntil,
  summaryRows,
  toStartRequest,
  validateDetails,
} from "./signup.model.ts";
import type { DetailsValues, SignupKind, SummaryRow } from "./signup.model.ts";

/**
 * Where the applicant is. A union rather than a step number plus a bag of
 * optional fields: a ticket only exists once a code has been sent, and a
 * result only once an account exists, so neither can be read too early.
 */
type Stage =
  | { kind: "details" }
  | { kind: "identity" }
  | { kind: "email"; ticket: VerificationTicket }
  | { kind: "done"; result: SignupResult };

export type SignupView =
  | {
      kind: "details";
      values: DetailsValues;
      passwordsMatch: boolean;
      /** False on an invited sign-up, which declares nothing. */
      asksMembership: boolean;
      error: string | null;
      busy: boolean;
    }
  | { kind: "identity"; busy: boolean; error: string | null }
  | {
      kind: "email";
      email: string;
      code: string;
      /** Seconds until the resend button becomes available; 0 when it already is. */
      resendIn: number;
      error: string | null;
      busy: boolean;
    }
  | {
      kind: "done";
      name: string;
      email: string;
      rows: SummaryRow[];
      bookCall: boolean;
      error: string | null;
      busy: boolean;
    };

export interface SignupController {
  view: SignupView;
  /** 0-based index of the active step, for the marker row. */
  step: number;
  complete: boolean;
  updateDetail: <K extends keyof DetailsValues>(
    field: K,
    value: DetailsValues[K],
  ) => void;
  /** Adds or removes one membership type. Its own handler so the view holds no list logic. */
  toggleMembership: (type: DeclaredMemberType) => void;
  submitDetails: () => void;
  backToDetails: () => void;
  sendCode: () => void;
  updateCode: (raw: string) => void;
  submitCode: () => void;
  resendCode: () => void;
  enterOndo: () => void;
}

export function useSignupController(): SignupController {
  // Read once, on mount. `?invite=<address>` says somebody asked this address to
  // confirm something, so a dormant account is already waiting to be claimed —
  // see `SignupKind`, and note that nothing security-bearing hangs on it.
  const [invited] = useState(invitedAddress);
  const kind: SignupKind = invited ? "invited" : "open";

  const [stage, setStage] = useState<Stage>({ kind: "details" });
  const [values, setValues] = useState<DetailsValues>(() =>
    invited
      ? // The address is prefilled AND the declaration emptied. Leaving the
        // default "collector" ticked would enrol a shipping clerk as a
        // collector purely because the form opened that way.
        { ...EMPTY_DETAILS, email: invited, declaredTypes: [] }
      : EMPTY_DETAILS,
  );
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Only ticks while a countdown is on screen. A permanent interval would
  // re-render the whole wizard once a second for the other three panels.
  useEffect(() => {
    if (stage.kind !== "email") return;

    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [stage.kind]);

  function updateDetail<K extends keyof DetailsValues>(
    field: K,
    value: DetailsValues[K],
  ): void {
    setValues((current) => ({ ...current, [field]: value }));
    // Clearing on edit rather than on the next submit, so the message goes
    // away as soon as the applicant acts on it.
    setError(null);
  }

  function toggleMembership(type: DeclaredMemberType): void {
    setValues((current) => ({
      ...current,
      declaredTypes: toggleDeclaredType(current.declaredTypes, type),
    }));
    setError(null);
  }

  function submitDetails(): void {
    const problem = validateDetails(values, kind);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setStage({ kind: "identity" });
  }

  function backToDetails(): void {
    setError(null);
    setStage({ kind: "details" });
  }

  /**
   * Leaves the identity step and asks the API to email a code.
   *
   * This is the first request of the whole flow — the details have been in the
   * browser until now. `identitySkipped` is hard-coded true because no
   * provider is wired up yet; the field exists so that flipping it is the only
   * change needed when one is.
   */
  function sendCode(): void {
    void run(async () => {
      const ticket = await startSignupVerification(toStartRequest(values, true, kind));
      setCode("");
      setNow(Date.now());
      setStage({ kind: "email", ticket });
    });
  }

  function updateCode(raw: string): void {
    setCode(sanitiseCode(raw));
    setError(null);
  }

  function submitCode(): void {
    if (stage.kind !== "email") return;
    if (!isCompleteCode(code)) {
      setError(SIGNUP_COPY.codeIncomplete);
      return;
    }

    const { ticket } = stage;
    void run(async () => {
      const result = await confirmSignupVerification({
        verificationId: ticket.verificationId,
        code: code.trim(),
        password: values.password,
      });
      setStage({ kind: "done", result });
    });
  }

  function resendCode(): void {
    if (stage.kind !== "email") return;

    const { ticket } = stage;
    void run(async () => {
      const next = await resendSignupVerification({
        verificationId: ticket.verificationId,
      });
      setCode("");
      setNow(Date.now());
      setStage({ kind: "email", ticket: next });
    });
  }

  /**
   * Signs in, which is what actually leaves this screen.
   *
   * Sign-in is deliberately held back until here rather than done the moment
   * the account exists: the session listener in App swaps the whole tree for
   * the workspace as soon as one appears, so signing in earlier would flash
   * past the confirmation panel the applicant is meant to read.
   */
  function enterOndo(): void {
    void run(async () => {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: values.email.trim(),
        password: values.password,
      });
      if (signInError) throw signInError;
      // No state change on success: the session listener re-renders the app
      // away from this screen, and `busy` is left set so the button does not
      // flash back to its idle label first.
    });
  }

  async function run(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await action();
      setBusy(false);
    } catch (caught) {
      setError(describeError(caught));
      setBusy(false);
    }
  }

  return {
    view: buildView(),
    step: stepIndex(stage),
    complete: stage.kind === "done",
    updateDetail,
    toggleMembership,
    submitDetails,
    backToDetails,
    sendCode,
    updateCode,
    submitCode,
    resendCode,
    enterOndo,
  };

  function buildView(): SignupView {
    switch (stage.kind) {
      case "details":
        return {
          kind: "details",
          values,
          passwordsMatch: passwordsMatch(values),
          // The form asks what somebody is only when nobody has already said.
          // An invited applicant was named in a verification request, which is
          // the whole of what ONDO needs from them.
          asksMembership: kind === "open",
          error,
          busy,
        };
      case "identity":
        return { kind: "identity", busy, error };
      case "email":
        return {
          kind: "email",
          email: stage.ticket.email,
          code,
          resendIn: secondsUntil(stage.ticket.resendAvailableAt, now),
          error,
          busy,
        };
      case "done":
        return {
          kind: "done",
          name: values.fullName.trim(),
          email: stage.result.email,
          rows: summaryRows(values, stage.result.ondoId),
          bookCall: values.bookCall === true,
          error,
          busy,
        };
    }
  }
}

function stepIndex(stage: Stage): number {
  switch (stage.kind) {
    case "details":
      return 0;
    case "identity":
      return 1;
    case "email":
      return 2;
    case "done":
      // Still the third step, just finished — SignupSteps ticks every marker
      // when `complete`, so the index only has to stop it going backwards.
      return 2;
  }
}
